import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ExternalLink, Mail, Phone } from "lucide-react";
import { useDataProvider, useTranslate } from "ra-core";
import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useCon36 } from "../datos/useFiltroEtapas";
import { enlaceLlamar, urlWeb } from "../dominio/contacto";
import {
  correoLejano,
  correosDeProspect,
  correosOrdenados,
  DIAS_CORREO_LEJANO,
  firmanteDe,
  hayAnalisis,
  numeroCapturas,
  primerCorreo,
  resumenCorreos,
  resumenParaLlamar,
  tipoCorreoConocido,
  tipoLeadConocido,
} from "../dominio/paraLlamar";
import type { Etapa, FichaProspect, Negocio } from "../dominio/tipos";

/** Etapas en las que «Para llamar» sale abierto (antes de la consultoría). */
const ETAPAS_PARA_LLAMAR: Etapa[] = [
  "nuevo",
  "en_conversacion",
  "no_contesta",
  "largo_plazo",
];
import { formatoFecha, formatoFechaHora } from "./formato";

type T = (k: string, o?: Record<string, unknown>) => string;

const Enlace = ({ href, texto }: { href: string; texto: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1 underline underline-offset-2"
  >
    {texto}
    <ExternalLink className="size-3" aria-hidden />
  </a>
);

const Bloque = ({
  titulo,
  children,
  testid,
}: {
  titulo: string;
  children: ReactNode;
  testid?: string;
}) => (
  <div className="space-y-1.5" data-testid={testid}>
    <h3 className="etiqueta">{titulo}</h3>
    {children}
  </div>
);

/** «fuente: su web (08/10/2026)» con enlace a la página de donde sale. */
const Fuente = ({
  t,
  fuente,
  url,
  fecha,
}: {
  t: T;
  fuente?: string | null;
  url?: string | null;
  fecha?: string | null;
}) => {
  const nombre = fuente === "borme" ? t("fuente_borme") : t("fuente_web");
  const texto = `${t("fuente")}: ${nombre}${fecha ? ` (${formatoFecha(fecha)})` : ""}`;
  return url ? <Enlace href={url} texto={texto} /> : <span>{texto}</span>;
};

/**
 * «Para llamar», la ficha del setter (Robert, 08/10/2026; contrato-crm-llamadas.md §10): lo
 * más parecido posible a la ficha del lead en Nevora Prospect, en su orden. Cabecera con tipo
 * y prioridad → dónde, dueño, teléfonos y email (con su fuente: decisión de Jan y Robert del
 * 08/10, sustituye a «teléfonos sin fuente» del 02/10) → resumen y caso → correo enviado →
 * análisis de su web → capturas. Todo sale de `negocios.prospect_ficha`; las capturas, por
 * enlaces firmados de 10 minutos (función `capturas`), nunca públicas.
 */
export const ParaLlamar = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const { ajustes } = useAjustes();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const con36 = useCon36();
  const deFicha: FichaProspect | null | undefined = negocio.prospect_ficha;
  const conFicha = !!deFicha || !!negocio.prospect_resumen;
  // Auditoría 30/10/2026: abierto mientras se llama; de la consultoría en adelante, plegado.
  const [abierto, setAbierto] = useState(
    ETAPAS_PARA_LLAMAR.includes(negocio.etapa),
  );
  // 36 (punto 10): los correos de verdad, con su firmante, si Prospect ya los da.
  const { data: deProspect = [] } = useQuery({
    queryKey: ["nevora", "correos-de-negocio", String(negocio.negocio_id)],
    queryFn: () => dataProvider.correosDeNegocio(negocio.negocio_id),
    enabled: con36 && conFicha && negocio.negocio_id != null,
    staleTime: 60 * 1000,
  });
  if (!conFicha) return null;
  const ficha: FichaProspect | null | undefined = deProspect.length
    ? { ...(deFicha ?? {}), correos: correosDeProspect(deProspect) }
    : deFicha;
  const t: T = (k, o) => translate(`nevora.para_llamar.${k}`, o);
  const prioridad = negocio.prospect_prioridad ?? ficha?.prioridad ?? null;
  const tipo = ficha?.tipo_lead;
  const correo = primerCorreo(ficha);
  const firmante = (() => {
    const primero = correosOrdenados(ficha).find((c) => c.enviado_at);
    return primero ? firmanteDe(primero) : null;
  })();
  const frio = !!negocio.prospect_frio_at;
  // 36: más de 7 días desde el primer correo → no abrir la llamada con él.
  const lejano = !frio && correoLejano(ficha, new Date());

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="superficie flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="para-llamar-plegado"
        aria-expanded={false}
      >
        <span className="min-w-0">
          <span className="titulo-seccion">{t("titulo")}</span>
          <span className="ml-2 text-sm text-muted-foreground">
            {t("plegado")}
          </span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </button>
    );
  }

  return (
    <section
      aria-label={t("titulo")}
      className="rounded-lg border border-[#3D8BD4]/40 bg-card p-4 md:p-5 space-y-4"
      data-testid="para-llamar"
    >
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="titulo-seccion">{t("titulo")}</h2>
          {tipo ? (
            <Badge data-testid="tipo-lead">
              {tipoLeadConocido(tipo) ? t(`tipos.${tipo}`) : tipo}
            </Badge>
          ) : null}
          {prioridad ? (
            <Badge variant="outline" title={t("prioridad_ayuda")}>
              {t("prioridad", { n: prioridad })}
            </Badge>
          ) : null}
          {ficha?.idioma ? (
            <Badge variant="outline">{ficha.idioma.toUpperCase()}</Badge>
          ) : null}
          {frio ? (
            <Badge
              variant="outline"
              className="border-amber-600 text-amber-800 dark:text-amber-300"
              title={t("frio_ayuda", { dias: ajustes.diasFrio })}
              data-testid="lead-frio"
            >
              {t("frio")}
            </Badge>
          ) : null}
          {lejano ? (
            <Badge
              variant="outline"
              className="border-amber-600 text-amber-800 dark:text-amber-300"
              title={t("no_abrir_correo_ayuda", { dias: DIAS_CORREO_LEJANO })}
              data-testid="correo-lejano"
            >
              {t("no_abrir_correo")}
            </Badge>
          ) : null}
        </div>
        {correo ? (
          <p
            className={
              frio || lejano
                ? "text-sm text-muted-foreground"
                : "text-sm font-medium"
            }
            data-testid="correo-cabecera"
          >
            <Mail
              className="mr-1.5 inline size-4 align-text-bottom"
              aria-hidden
            />
            {correo.buzon
              ? t("correo_cabecera", {
                  fecha: formatoFecha(correo.fecha),
                  buzon: correo.buzon,
                })
              : t("correo_cabecera_sin_buzon", {
                  fecha: formatoFecha(correo.fecha),
                })}
            {firmante ? ` · ${t("firmado_por", { quien: firmante })}` : ""}
          </p>
        ) : null}
        {correo && (frio || lejano) ? (
          // 39 (Robert, 23/10/2026): sin tachar; se explica por qué no nombrarlo en la llamada.
          <p
            className="text-sm text-amber-800 dark:text-amber-300"
            data-testid="no-mencionar-correo"
          >
            {t("no_mencionar_correo", { fecha: formatoFecha(correo.fecha) })}
          </p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          {[
            prioridad && [1, 2, 3].includes(Number(prioridad))
              ? t(`prioridad_${prioridad}`)
              : null,
            [ficha?.sector, ficha?.municipio].filter(Boolean).join(" · "),
          ]
            .filter(Boolean)
            .join(" — ")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Donde ficha={ficha} t={t} />
        <Dueno ficha={ficha} t={t} />
        <Telefonos ficha={ficha} t={t} />
        <EmailPublico ficha={ficha} t={t} />
      </div>

      <Resumen
        texto={resumenParaLlamar(ficha, negocio.prospect_resumen)}
        caso={ficha?.caso}
        t={t}
      />
      <CorreoEnviado ficha={ficha} t={t} />
      <Analisis ficha={ficha} t={t} />
      <Capturas negocio={negocio} ficha={ficha} t={t} />
    </section>
  );
};

const Donde = ({ ficha, t }: { ficha?: FichaProspect | null; t: T }) => {
  const web = urlWeb(ficha?.web ?? null);
  const instagram = ficha?.instagram
    ? ficha.instagram.startsWith("http")
      ? ficha.instagram
      : `https://www.instagram.com/${ficha.instagram.replace(/^@/, "")}`
    : null;
  if (!ficha?.direccion && !web && !ficha?.maps_url && !instagram) return null;
  return (
    <Bloque titulo={t("donde")}>
      {ficha?.direccion ? <p className="text-sm">{ficha.direccion}</p> : null}
      <div className="flex flex-wrap gap-3 text-sm">
        {web ? <Enlace href={web} texto={t("web")} /> : null}
        {ficha?.maps_url ? (
          <Enlace href={ficha.maps_url} texto={t("maps")} />
        ) : null}
        {instagram ? <Enlace href={instagram} texto={t("instagram")} /> : null}
      </div>
    </Bloque>
  );
};

const Dueno = ({ ficha, t }: { ficha?: FichaProspect | null; t: T }) => {
  const d = ficha?.dueno;
  if (!d?.nombre) return null;
  return (
    <Bloque titulo={t("dueno")} testid="dueno">
      <p className="text-sm font-medium">
        {d.nombre}
        {d.cargo ? ` · ${d.cargo}` : ""}
      </p>
      <p className="text-xs text-muted-foreground">
        <Fuente t={t} fuente={d.fuente} url={d.fuente_url} fecha={d.fecha} />
        {d.confianza ? ` · ${t(`confianza_${d.confianza}`)}` : ""}
      </p>
    </Bloque>
  );
};

const Telefonos = ({ ficha, t }: { ficha?: FichaProspect | null; t: T }) => {
  const telefonos = ficha?.telefonos ?? [];
  if (!telefonos.length) return null;
  return (
    <Bloque titulo={t("telefonos")} testid="telefonos">
      <ul className="space-y-2">
        {telefonos.map((tel, i) => {
          const href = enlaceLlamar(tel.numero);
          return (
            <li
              key={`${tel.numero}-${i}`}
              className="rounded-md border px-3 py-2 text-sm"
              title={tel.evidencia ?? undefined}
            >
              <div className="flex flex-wrap items-center gap-2">
                {href ? (
                  <a
                    href={href}
                    className="inline-flex items-center gap-1.5 font-medium"
                  >
                    <Phone className="size-4" aria-hidden />
                    {tel.numero}
                  </a>
                ) : (
                  <span className="font-medium">{tel.numero}</span>
                )}
                {tel.tipo ? (
                  <Badge variant="secondary">{t(`tipo_${tel.tipo}`)}</Badge>
                ) : null}
                {tel.de_quien ? (
                  <Badge variant="outline">{t(`de_${tel.de_quien}`)}</Badge>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                <Fuente
                  t={t}
                  fuente={tel.fuente}
                  url={tel.fuente_url}
                  fecha={tel.fecha}
                />
              </p>
              {tel.evidencia ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  «{tel.evidencia}»
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Bloque>
  );
};

const EmailPublico = ({ ficha, t }: { ficha?: FichaProspect | null; t: T }) => {
  if (!ficha) return null;
  const e = ficha.email_negocio;
  return (
    <Bloque titulo={t("email")}>
      {e?.email ? (
        <>
          <a
            href={`mailto:${e.email}`}
            className="text-sm underline underline-offset-2"
          >
            {e.email}
          </a>
          <p className="text-xs text-muted-foreground">
            {e.fuente_url ? (
              <Enlace
                href={e.fuente_url}
                texto={t("email_publico", {
                  fecha: e.fecha ? formatoFecha(e.fecha) : "",
                })}
              />
            ) : (
              t("email_publico", {
                fecha: e.fecha ? formatoFecha(e.fecha) : "",
              })
            )}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t("sin_email")}</p>
      )}
    </Bloque>
  );
};

const Resumen = ({
  texto,
  caso,
  t,
}: {
  texto: string | null;
  caso?: string | null;
  t: T;
}) => {
  if (!texto && !caso) return null;
  return (
    <Bloque titulo={t("resumen")} testid="resumen">
      {texto ? <p className="whitespace-pre-wrap text-sm">{texto}</p> : null}
      {caso ? (
        <p className="text-sm">
          <span className="text-muted-foreground">{t("caso")}: </span>
          {caso}
        </p>
      ) : null}
    </Bloque>
  );
};

const CorreoEnviado = ({
  ficha,
  t,
}: {
  ficha?: FichaProspect | null;
  t: T;
}) => {
  const resumen = resumenCorreos(ficha);
  if (!resumen) {
    // Fichas anteriores al 08/10: solo sabíamos si Nevora le había escrito.
    return ficha?.email_nevora?.enviado ? (
      <Bloque titulo={t("correo_enviado")}>
        <p className="text-sm">
          {t("email_nevora", {
            fecha: ficha.email_nevora.fecha
              ? formatoFecha(ficha.email_nevora.fecha)
              : "",
          })}
        </p>
      </Bloque>
    ) : null;
  }
  const correos = correosOrdenados(ficha);
  return (
    <Bloque titulo={t("correo_enviado")} testid="correo-enviado">
      {resumen.n === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(
            ficha?.email_negocio?.email?.trim()
              ? "sin_correos_con_email"
              : "sin_correos",
          )}
        </p>
      ) : (
        <>
          <p className="text-sm">
            {t("n_correos", { smart_count: resumen.n })} ·{" "}
            {resumen.recordatorio !== null
              ? t("con_recordatorio", {
                  fecha: resumen.recordatorio
                    ? formatoFecha(resumen.recordatorio)
                    : "",
                })
              : t("sin_recordatorio")}
          </p>
          <div className="space-y-2">
            {correos.map((c, i) => (
              <details
                key={`${c.enviado_at}-${i}`}
                open={i === 0}
                className="group rounded-md border px-3 py-2"
              >
                <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 text-sm">
                  <ChevronRight
                    className="size-4 transition-transform group-open:rotate-90"
                    aria-hidden
                  />
                  <Mail className="size-4 text-muted-foreground" aria-hidden />
                  <span className="font-medium">
                    {tipoCorreoConocido(c.tipo)
                      ? t(`correo_${c.tipo}`)
                      : t("correo")}
                  </span>
                  {c.enviado_at ? (
                    <span className="text-muted-foreground">
                      {formatoFechaHora(c.enviado_at)}
                    </span>
                  ) : null}
                  {c.buzon ? (
                    <span className="text-muted-foreground">
                      · {t("desde", { buzon: c.buzon })}
                    </span>
                  ) : null}
                  {firmanteDe(c) ? (
                    <span className="text-muted-foreground">
                      · {t("firmado_por", { quien: firmanteDe(c) })}
                    </span>
                  ) : null}
                </summary>
                <div className="mt-2 space-y-2 text-sm">
                  {c.asunto ? (
                    <p>
                      <span className="text-muted-foreground">
                        {t("asunto")}:{" "}
                      </span>
                      <span className="font-medium">{c.asunto}</span>
                    </p>
                  ) : null}
                  {c.cuerpo ? (
                    <p className="whitespace-pre-wrap rounded bg-muted/50 p-3">
                      {c.cuerpo}
                    </p>
                  ) : null}
                  {c.destino ? (
                    <p className="text-xs text-muted-foreground">
                      {t("enviado_a", { destino: c.destino })}
                    </p>
                  ) : null}
                </div>
              </details>
            ))}
          </div>
        </>
      )}
    </Bloque>
  );
};

const Analisis = ({ ficha, t }: { ficha?: FichaProspect | null; t: T }) => {
  if (!hayAnalisis(ficha)) return null;
  const a = ficha!.analisis!;
  const hechos = a.hechos ?? [];
  const senales = a.senales ?? [];
  return (
    <Bloque titulo={t("analisis")} testid="analisis">
      {a.resumen ? <p className="text-sm">{a.resumen}</p> : null}
      {a.sector?.veredicto ? (
        <p className="text-sm">
          <span className="text-muted-foreground">{t("sector")}: </span>
          {["si", "no", "dudoso"].includes(a.sector.veredicto)
            ? t(`sector_${a.sector.veredicto}`)
            : a.sector.veredicto}
          {a.sector.motivo ? ` — ${a.sector.motivo}` : ""}
        </p>
      ) : null}
      {senales.length ? (
        <ul className="space-y-1 text-sm">
          {senales.map((s, i) => (
            <li key={`${s.clave}-${i}`}>
              <span className="font-medium">{s.etiqueta ?? s.clave}</span>
              {s.evidencia ? ` — ${s.evidencia}` : ""}
              {s.fuente_url ? (
                <>
                  {" · "}
                  <Enlace href={s.fuente_url} texto={t("ver_fuente")} />
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {hechos.length ? (
        <details className="group rounded-md border px-3 py-2">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
            <ChevronRight
              className="size-4 transition-transform group-open:rotate-90"
              aria-hidden
            />
            {t("hechos", { smart_count: hechos.length })}
          </summary>
          <ul className="mt-2 space-y-2 text-sm">
            {hechos.map((h, i) => (
              <li key={i}>
                <p className="font-medium">{h.hecho}</p>
                {h.fragmento ? (
                  <p className="text-xs text-muted-foreground">
                    «{h.fragmento}»
                  </p>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  {h.fuente_url ? (
                    <Enlace href={h.fuente_url} texto={t("ver_fuente")} />
                  ) : null}
                  {h.fecha ? ` · ${formatoFecha(h.fecha)}` : ""}
                  {h.confianza
                    ? ` · ${["confirmado", "estimado"].includes(h.confianza) ? t(`dato_${h.confianza}`) : h.confianza}`
                    : ""}
                </p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Bloque>
  );
};

const Capturas = ({
  negocio,
  ficha,
  t,
}: {
  negocio: Negocio;
  ficha?: FichaProspect | null;
  t: T;
}) => {
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const hay = numeroCapturas(ficha) > 0;
  const [grande, setGrande] = useState<{ url: string; tipo: string } | null>(
    null,
  );
  const { data, isPending, isError } = useQuery({
    queryKey: ["nevora", "capturas", negocio.negocio_id],
    queryFn: () => dataProvider.capturasProspect(negocio.negocio_id),
    enabled: hay,
    // Los enlaces caducan a los 10 minutos: se piden de nuevo antes.
    staleTime: 5 * 60 * 1000,
    gcTime: 8 * 60 * 1000,
    retry: false,
  });
  if (!hay) return null;
  const nombre = (tipo: string) =>
    ["portada", "contacto"].includes(tipo) ? t(`captura_${tipo}`) : tipo;
  return (
    <Bloque titulo={t("capturas")} testid="capturas">
      {isPending ? (
        <div className="flex gap-3">
          <Skeleton className="h-24 w-36" />
          <Skeleton className="h-24 w-36" />
        </div>
      ) : isError || !data?.some((c) => c.url) ? (
        <p className="text-sm text-muted-foreground">
          {t("capturas_no_disponibles")}
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {data.map((c, i) =>
            c.url ? (
              <button
                key={`${c.tipo}-${i}`}
                type="button"
                onClick={() => setGrande({ url: c.url!, tipo: c.tipo })}
                className="space-y-1 text-left"
              >
                <img
                  src={c.url}
                  alt={nombre(c.tipo)}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-24 w-36 rounded-md border object-cover object-top"
                />
                <span className="block text-xs text-muted-foreground">
                  {nombre(c.tipo)}
                </span>
              </button>
            ) : null,
          )}
        </div>
      )}
      <Dialog open={!!grande} onOpenChange={(a) => !a && setGrande(null)}>
        <DialogContent className="max-w-4xl">
          <DialogTitle>{grande ? nombre(grande.tipo) : ""}</DialogTitle>
          {grande ? (
            <img
              src={grande.url}
              alt={nombre(grande.tipo)}
              referrerPolicy="no-referrer"
              className="max-h-[75vh] w-full rounded-md object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Bloque>
  );
};
